class AdawatNewsletterPopup extends HTMLElement {
  static storageKey = 'adawat-nl-dismissed';
  static ttlMs = 14 * 24 * 60 * 60 * 1000;

  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    if (this.parentElement && this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    this.dialog = this.querySelector('.adawat-nl__dialog');
    this.backdrop = this.querySelector('[data-nl-close-backdrop]');
    this.closeBtns = this.querySelectorAll('[data-nl-close]');
    this.form = this.querySelector('form');
    this.email = this.querySelector('input[type="email"]');
    this.delay = Number(this.dataset.delay || 1200);
    this.posted = this.dataset.posted === 'true';
    this.forceOpen = this.dataset.forceOpen === 'true';
    this.designMode = this.isDesignMode();

    this.closeBtns.forEach((btn) => this.bindTap(btn, () => this.dismiss(true)));
    if (this.backdrop) this.bindTap(this.backdrop, () => this.dismiss(true));

    this.copyBtn = this.querySelector('[data-nl-copy]');
    this.codeEl = this.querySelector('[data-nl-code]');
    if (this.copyBtn && this.codeEl) {
      this.copyBtn.addEventListener('click', () => this.copyCode());
    }
    this.applyBtn = this.querySelector('[data-nl-apply]');
    if (this.applyBtn) {
      this.applyBtn.addEventListener('click', (event) => this.applyAndShop(event));
    }

    this._onKey = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        this.dismiss(true);
      }
    };
    document.addEventListener('keydown', this._onKey);

    if (this.form) {
      this.form.addEventListener('submit', () => {
        this.persist();
      });
    }

    if (this.posted) {
      this.persist();
      this.open();
      this.applyDiscountCookie();
      return;
    }

    if (this.forceOpen || this.designMode) {
      this.open();
      return;
    }

    if (this.isDismissed()) {
      this.hide();
      return;
    }

    this._timer = window.setTimeout(() => this.open(), Number.isFinite(this.delay) ? this.delay : 1200);
  }

  disconnectedCallback() {
    window.clearTimeout(this._timer);
    document.removeEventListener('keydown', this._onKey);
    this.unlockScroll();
  }

  isDesignMode() {
    return Boolean(window.Shopify?.designMode) || document.documentElement.classList.contains('shopify-design-mode');
  }

  bindTap(el, fn) {
    if (!el) return;
    let last = 0;
    const run = (event) => {
      const now = Date.now();
      if (now - last < 450) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      last = now;
      event.preventDefault();
      event.stopPropagation();
      fn(event);
    };
    el.addEventListener('click', run);
    el.addEventListener('pointerup', run);
    el.addEventListener('touchend', run, { passive: false });
  }

  isDismissed() {
    try {
      const raw = window.localStorage.getItem(AdawatNewsletterPopup.storageKey);
      if (!raw) return false;
      const ts = Number(raw);
      if (!Number.isFinite(ts)) return true;
      return Date.now() - ts < AdawatNewsletterPopup.ttlMs;
    } catch (_) {
      return false;
    }
  }

  persist() {
    try {
      window.localStorage.setItem(AdawatNewsletterPopup.storageKey, String(Date.now()));
    } catch (_) {
      /* ignore */
    }
  }

  applyDiscountCookie() {
    const code = (this.dataset.discountCode || this.codeEl?.textContent || '').trim();
    if (!code) return;
    try {
      window.localStorage.setItem('adawat-nl-discount', code);
    } catch (_) {
      /* ignore */
    }
  }

  async applyAndShop(event) {
    event.preventDefault();
    event.stopPropagation();
    const code = (this.dataset.discountCode || this.codeEl?.textContent || '').trim();
    this.applyDiscountCookie();
    const btn = this.applyBtn;
    const original = btn?.textContent;
    const rtl = document.documentElement.dir === 'rtl';
    if (btn) {
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = rtl ? 'جاري التطبيق…' : 'Applying…';
    }
    try {
      if (window.AdawatCart?.applyDiscount && code) {
        await window.AdawatCart.applyDiscount(code, { openDrawer: true });
        this.dismiss(true);
        return;
      }
    } catch (_) {
      /* fall through to full page apply */
    }
    window.location.href = btn?.getAttribute('href') || `/discount/${encodeURIComponent(code)}?redirect=/`;
    if (btn && original) btn.textContent = original;
  }

  async copyCode() {
    const code = (this.codeEl?.textContent || this.dataset.discountCode || '').trim();
    if (!code || !this.copyBtn) return;
    const original = this.copyBtn.textContent;
    try {
      await navigator.clipboard.writeText(code);
      this.copyBtn.textContent = this.copyBtn.closest('[dir="rtl"]') || document.documentElement.dir === 'rtl' ? 'تم' : 'Copied';
    } catch (_) {
      const range = document.createRange();
      range.selectNodeContents(this.codeEl);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      this.copyBtn.textContent = original;
      return;
    }
    window.setTimeout(() => {
      this.copyBtn.textContent = original;
    }, 1600);
  }

  open() {
    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    this.lockScroll();

    const isTouch = 'ontouchstart' in window && navigator.maxTouchPoints > 0;
    window.setTimeout(() => {
      if (isTouch) {
        this.querySelector('[data-nl-close]')?.focus({ preventScroll: true });
      } else {
        this.email?.focus({ preventScroll: true });
      }
    }, 280);
  }

  dismiss(persist) {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.unlockScroll();
    if (persist && !this.designMode) this.persist();
    window.setTimeout(() => {
      if (!this.classList.contains('is-open')) this.hide();
    }, 320);
    try {
      window.dispatchEvent(
        new CustomEvent('adawat:newsletter-closed', {
          detail: { persist: Boolean(persist), posted: Boolean(this.posted) },
        })
      );
    } catch (_) {
      /* ignore */
    }
  }

  hide() {
    this.setAttribute('hidden', '');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
  }

  lockScroll() {
    if (this._locked) return;
    this._locked = true;
    this._scrollY = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${this._scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (!this._locked) return;
    this._locked = false;
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    document.documentElement.style.overflow = '';
    window.scrollTo(0, this._scrollY || 0);
  }
}

if (!customElements.get('adawat-newsletter-popup')) {
  customElements.define('adawat-newsletter-popup', AdawatNewsletterPopup);
}
